import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep } from "@/constants/inputs";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";
import { getFloat } from "@/utils/mappings";

import ExpandableSection from "../../../../components/job/ExpandableSection";

const cardName = "pressureCyclesSection";
const pressureCyclesSectionSchema = z
  .object({
    number_of_cycles: z.union([
      z.number().gte(7000),
      z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
    ]),
    min_cycle_pressure: z.union([
      z.number().gte(0),
      z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
    ]),
    max_cycle_pressure: z.union([
      z.number(),
      z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
    ]),
  })
  .superRefine((data, context) => {
    if (data.max_cycle_pressure <= data.min_cycle_pressure) {
      context.addIssue({
        code: "custom",
        message: "max cycle pressure should be greater then min cycle pressure",
        path: ["max_cycle_pressure"],
      });
    }
  });

type PressureCyclesSchema = z.infer<typeof pressureCyclesSectionSchema>;
export function PressureCycles() {
  const { sections, toggleExpandableSection, setPressureCyclesSection } = useCUCompositeJobStore();
  const savedData = sections.pressureCyclesSection?.data;

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<PressureCyclesSchema>({
    resolver: zodResolver(pressureCyclesSectionSchema),
    values: {
      number_of_cycles: savedData?.number_of_cycles ?? "",
      min_cycle_pressure: savedData?.min_cycle_pressure ?? "",
      max_cycle_pressure: savedData?.max_cycle_pressure ?? "",
    },
  });

  return (
    <ExpandableSection
      status={sections.pressureCyclesSection?.status ?? "hidden"}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Pressure Cycles"
    >
      <div>
        <form
          className="flex flex-col flex-wrap items-center gap-6 md:flex-row md:gap-20"
          onSubmit={handleSubmit((data) => {
            setPressureCyclesSection({ data, status: "done" });
          })}
        >
          <TextInput
            className="flex-1 basis-full md:basis-60"
            label="Number of cycles"
            type="number"
            step={inputStep}
            error={errors.number_of_cycles?.message}
            {...register("number_of_cycles", { setValueAs: getFloat })}
          />
          <TextInput
            className="flex-1 basis-full md:basis-60"
            label="Min cycle pressure"
            type="number"
            step={inputStep}
            error={errors.min_cycle_pressure?.message}
            {...register("min_cycle_pressure", { setValueAs: getFloat })}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                PSI
              </Typography>
            }
          />
          <TextInput
            className="flex-1 basis-full md:basis-60"
            label="Max cycle pressure"
            type="number"
            step={inputStep}
            error={errors.max_cycle_pressure?.message}
            {...register("max_cycle_pressure", { setValueAs: getFloat })}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                PSI
              </Typography>
            }
          />
          <Button className="w-full self-end md:w-fit" type="submit">
            Save
          </Button>
        </form>
      </div>
    </ExpandableSection>
  );
}
